<template>
  <div v-on="$listeners"
       :style="containerStyle">
    <div ref="graph" />
  </div>
</template>

<script>
  /*
  https://github.com/vasturiano/3d-force-graph
   */
  import ForceGraph from 'force-graph'
  import ForceGraph3d from '3d-force-graph'
  import { forceCenter, forceManyBody } from 'd3-force-3d'
  import { forceSimulation } from 'd3-force-3d'
  import { ClickCounter } from '@js/interface-utils'
  import { FontStyle } from '@js/styles'
  import { mapGetters } from 'vuex'
  import { truncate } from '@js/str-utils'

  function getValue(obj, key, defaultValue) {
    return obj ? (obj[key] ? obj[key] : defaultValue) : defaultValue;
  }

  function isObject(value) {
    return typeof value === 'object';
  }

  function getStyleValue(isObjectFlags, style, newStyle, key, state) {
    return isObjectFlags[key] ? (newStyle[key] ? (newStyle[key][state] ? newStyle[key][state] : (style[key] ? style[key] : null)) : (style[key] ? style[key] : null)) : (style[key] ? style[key] : null);
  }

  export default {
    //Eg.)
    //nodes: [{ id: 1, name: 'Python', years: 3, neighbors: [2, 3, 5,], }, ...]
    //links: [{ source: 1, target: 2, }, ...]
    name: 'BaseGraph',
    inheritAttrs: false,
    props: {
      backgroundColor: { type: String, required: false, default: 'rgba(255, 255, 255, 0.0)' },
      containerStyle: { type: Object, required: false, default: () => {}},
      disableDoubleClickFitView: { type: Boolean, required: false, default: false },
      disablePopover: { type: Boolean, required: false, default: false },
      doubleClickFitViewInterval: { type: Number, required: false, default: 300 },
      extraIndex: { type: String, required: false, default: '0' },
      graphStyle: { type: Object, required: false, default: () => {}},
      height: { type: Number, required: false, default: 480 },
      hideLabel: { type: Boolean, required: false, default: false },
      is3d: { type: Boolean, required: false, default: false },
      isHighlighted: { type: Function, required: false, default: (node) => { return false; } },
      labelMaxLength: { type: Number, required: false, default: -1 },
      links: { type: Array, required: true },
      neighborsKeyName: { type: String, required: false, default: 'neighbors' },
      nodes: { type: Array, required: true },
      popover: { type: Function, required: false, default: node => {
        return String.raw`<div>${node.id}: ${node.name}</div>`;
      }},
      selectedNodeIds: { type: Array, required: false, default: () => [] },
      styleAttributes: { type: Array, required: false, default: () => [] },
      triggerEdgeUpdates: { type: Boolean, required: false, default: false },
      triggerFitView: { type: Boolean, required: false, default: false },
      triggerNodeUpdates: { type: Boolean, required: false, default: false },
      width: { type: Number, required: false, default: 640 },
    },
    data: function () {
      return {
        clickCounter: new ClickCounter(['background']),
        cooldownTicks: getValue(this.graphStyle, 'cooldownTicks', 20),
        cursorOnHover: getValue(this.graphStyle, 'cursorOnHover', 'pointer'),
        defaultLabelAngle: getValue(this.graphStyle, 'defaultLabelAngle', 90.0),
        distanceMax: getValue(this.graphStyle, 'distanceMax', 60),
        fitDuration: getValue(this.graphStyle, 'fitDuration', 200),
        fitPadding: getValue(this.graphStyle, 'fitPadding', 40),
        fontColor: getValue(this.graphStyle, 'fontColor', null),
        fontColorOnHover: getValue(this.graphStyle, 'fontColorOnHover', null),
        fontFamily: getValue(this.graphStyle, 'fontFamily', 'sans-serif'),
        fontSize: getValue(this.graphStyle, 'fontSize', '12px'),
        fontWeight: getValue(this.graphStyle, 'fontWeight', 'normal'),
        hoverWithNeighbors: getValue(this.graphStyle, 'hoverWithNeighbors', false),
        initCooldownTicks: getValue(this.graphStyle, 'initCooldownTicks', 30),
        initWarmupTicks: getValue(this.graphStyle, 'initWarmupTicks', 50),
        isObjectFlags: {},
        labelMargin: getValue(this.graphStyle, 'labelMargin', 1),
        linkWidth: getValue(this.graphStyle, 'linkWidth', 2),
        linkHoverPrecision: getValue(this.graphStyle, 'linkHoverPrecision', 40),
        maxZoom: getValue(this.graphStyle, 'maxZoom', 20.0),
        neighborsOnHover: [],
        nodeColor: getValue(this.graphStyle, 'nodeColor', '#E0E0E0'),
        nodeId: getValue(this.graphStyle, 'nodeId', 'id'),
        nodeOnHover: null,
        nodeRadius: getValue(this.graphStyle, 'nodeRadius', 10),
        nodeRadiusFunction: getValue(this.graphStyle, 'nodeRadiusFunction', null),
        ringColor: getValue(this.graphStyle, 'ringColor', null),
        ringWidth: getValue(this.graphStyle, 'ringWidth', 0),
        warmupTicks: getValue(this.graphStyle, 'warmupTicks', 0),
        graph: null,
        labelAngles: null,
        nodeStyles: {
          fontColor: getValue(this.graphStyle, 'fontColor', null),
          fontSize: getValue(this.graphStyle, 'fontSize', '12px'),
          fontWeight: getValue(this.graphStyle, 'fontWeight', 'normal'),
          nodeColor: getValue(this.graphStyle, 'nodeColor', '#E0E0E0'),
          ringColor: getValue(this.graphStyle, 'ringColor', null),
          ringWidth: getValue(this.graphStyle, 'ringWidth', 0),
        },
        nodeStyleKeys: [],
        skipWatching: 0,
        selectedNodeIdsUpdated: false,
      };
    },
    created: function () {
      this.labelAngles = new Map();
      const defaultAngle = this.defaultLabelAngle / 180.0 * Math.PI;
      this.nodes.forEach(elem => {
        this.labelAngles.set(elem.id, defaultAngle);
      });

      this.nodeStyleKeys = Object.keys(this.nodeStyles);
      this.isObjectFlags = this.nodeStyleKeys.reduce((result, elem) => {
        result[elem] = isObject(this.nodeStyles[elem]);
        return result;
      }, {});
    },
    mounted: async function () {
      await this.initGraph();
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      graphData: function () {
        return {
          nodes: this.nodes,
          links: this.links,
        };
      },
    },
    methods: {
      atan2: function (y, x) {
        const rad = Math.atan2(y, x);
        return rad < 0 ? rad + (2 * Math.PI) : rad;
      },
      getNodeStyle: function (node) {
        const style = {
          hover: false,
          hoverNeighbor: false,
          highlight: false,
          ...this.nodeStyleKeys.reduce((result, elem) => {
            result[elem] = null;
            return result;
          }, {}),
        };

        this.nodeStyleKeys.forEach(key => {
          style[key] = getStyleValue(this.isObjectFlags, style, this.nodeStyles, key, 'default');
        });

        if (this.isHighlighted(node)) {
          this.nodeStyleKeys.forEach(key => {
            style[key] = getStyleValue(this.isObjectFlags, style, this.nodeStyles, key, 'highlight');
          });
        }

        if (this.isOnHover(node)) {
          this.nodeStyleKeys.forEach(key => {
            style[key] = getStyleValue(this.isObjectFlags, style, this.nodeStyles, key, 'hover');
          });
        } else if (this.isOnHoverNeighbor(node)) {
          this.nodeStyleKeys.forEach(key => {
            style[key] = getStyleValue(this.isObjectFlags, style, this.nodeStyles, key, 'hoverNeighbor');
          });
        }

        if (this.selectedNodeIds.includes(node[this.nodeId])) {
          this.nodeStyleKeys.forEach(key => {
            style[key] = getStyleValue(this.isObjectFlags, style, this.nodeStyles, key, 'selected');
          });
        }

        return style;
      },
      findWidestAngle: function (angles) {
        if (angles.length === 0) {
          return this.defaultLabelAngle / 180 * Math.PI;
        } else if (angles.length === 1) {
          return angles[0][1] + Math.PI;
        }

        const extendedAngles = [
          [this.extraIndex, angles[angles.length - 1][1] - 2*Math.PI], ...angles];

        const dangles = extendedAngles.slice(1, extendedAngles.length).reduce(
            (result, elem, index) => {
          result.push(elem[1] - extendedAngles[index][1]);
          return result;
        }, []);

        const index = dangles.indexOf(Math.max(...dangles));
        return extendedAngles[index][1] + dangles[index] * 0.5;
      },
      fitGraphToCanvas: function (fitDuration = this.fitDuration) {
        this.graph.zoomToFit(fitDuration, this.fitPadding);
      },
      initGraph: function () {
        this.graph = null;
        const forceGraph = this.is3d ? ForceGraph3d : ForceGraph;

        this.graph = forceGraph()(this.$refs.graph)
          .graphData(this.graphData)
          .width(this.width)
          .height(this.height)
          .d3Force('charge', forceManyBody().distanceMax(this.distanceMax))
          .maxZoom(this.maxZoom)
          .warmupTicks(this.initWarmupTicks)
          .cooldownTicks(this.initCooldownTicks)
          .nodeLabel(this.disablePopover ? null : this.popover)
          .nodeId(this.nodeId)
          .nodeRelSize(this.nodeRadius)
          .nodeCanvasObject(this.nodeCanvasObject)
          .linkWidth(this.linkWidth)
          .linkHoverPrecision(40)
          .onNodeHover(this.onNodeHover)
          .onNodeClick(this.onNodeClick)
          .onNodeRightClick(this.onNodeRightClick)
          .onLinkRightClick(this.onLinkRightClick)
          .onBackgroundClick(this.onBackgroundClick)
          .onBackgroundRightClick(this.onBackgroundRightClick)
          .onEngineTick(this.onEngineTick)
          .onEngineStop(() => {
            this.onEngineTick();
            this.fitGraphToCanvas();
          });

        setTimeout(() => {
          this.graph.warmupTicks(this.warmupTicks);
          this.graph.cooldownTicks(this.cooldownTicks);
          this.graph.onEngineStop(() => {});
        }, 1000);
      },
      isOnHover: function (node) {
        return node === this.nodeOnHover;
      },
      isOnHoverNeighbor: function (node) {
        return this.neighborsOnHover ? this.neighborsOnHover.includes(node.id) : false;
      },
      nodeCanvasObject: function (node, ctx, globalScale) {
        const style = this.getNodeStyle(node);

        const label = this.labelMaxLength > 0 ? truncate(node.name, this.labelMaxLength) : node.name;
        const fontSize = parseInt(style.fontSize) / globalScale;
        ctx.font = `${style.fontWeight} ${fontSize}px ${this.fontFamily}`;
        const textWidth = ctx.measureText(label).width;
        const textHeight = fontSize;
        const nodeRadius = this.nodeRadiusFunction ? this.nodeRadiusFunction(node) / globalScale : this.nodeRadius / globalScale;

        if (!this.hideLabel) {
          const semiMajorAxis = nodeRadius + textWidth * 0.5 + this.labelMargin;
          const semiMinorAxis = nodeRadius + textHeight * 0.5 + this.labelMargin;
          const rad = this.labelAngles[node.id];
          const labelX = node.x + semiMajorAxis * Math.cos(rad);
          const labelY = node.y + semiMinorAxis * Math.sin(rad);

          ctx.fillStyle = style.fontColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, labelX, labelY);

          const topLeftX = Math.min(
              node.x - nodeRadius, labelX - textWidth * 0.5 - this.labelMargin);
          const topLeftY = Math.min(
              node.y - nodeRadius, labelY - textHeight * 0.5 - this.labelMargin);
          const bottomRightX = Math.max(
              node.x + nodeRadius, labelX + textWidth * 0.5 + this.labelMargin);
          const bottomRightY = Math.max(
              node.y + nodeRadius, labelY + textHeight * 0.5 + this.labelMargin);
          const boundingBoxWidth = bottomRightX - topLeftX;
          const boundingBoxHeight = bottomRightY - topLeftY;

          ctx.fillStyle = this.backgroundColor;
          ctx.fillRect(topLeftX, topLeftY, boundingBoxWidth, boundingBoxHeight);
        }


        if (style.ringWidth && style.ringWidth > 0) {
          ctx.fillStyle = style.ringColor
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius + parseInt(style.ringWidth) / globalScale, 0, 2 *Math.PI, false);
          ctx.fill();
        }

        ctx.fillStyle = style.nodeColor;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
        ctx.fill();
      },
      onBackgroundClick: function () {
        this.clickCounter.click('background');
        if (this.clickCounter.isDoubleClicked('background', false) && !this.disabledDoubleClickFitView) {
          this.fitGraphToCanvas();
        }
      },
      onEdgeUpdate: function () {
        this.graph.graphData(this.graphData);
      },
      onEngineTick: function () {
        if (!this.hideLabel) {
          this.updateLabelAngles();
        }
      },
      onNodeClick: function (node) {
        this.$emit('click-node', node);
      },
      onNodeHover: function (node) {
        this.$refs.graph.style.cursor = node ? this.cursorOnHover : null;
        this.nodeOnHover = null;
        this.neighborsOnHover = [];

        if (node) {
          this.nodeOnHover = node;
          this.neighborsOnHover = node[this.neighborsKeyName];
        }

        this.graph.nodeCanvasObject(this.nodeCanvasObject)
      },
      onNodeUpdate: function () {
        this.graph.graphData(this.graphData)
          .warmupTicks(this.initWarmupTicks)
          .cooldownTicks(this.initCooldownTicks)
          .onEngineStop(() => {
            this.onEngineTick();
            this.fitGraphToCanvas(this.fitDuration);
          });

        setTimeout(() => {
          this.graph.warmupTicks(this.warmupTicks);
          this.graph.cooldownTicks(this.cooldownTicks);
          this.graph.onEngineStop(() => {});
        }, 1000);
      },
      updateLabelAngles: function () {
        for (const node of this.graph.graphData()['nodes']) {
          let neighborAngles = this.graph.graphData()['nodes']
              .filter(elem => node[this.neighborsKeyName] ? node[this.neighborsKeyName].includes(elem.id) : false)
              .reduce((result, elem) => {
                result[elem.id] = this.atan2(elem.y - node.y, elem.x - node.x);
                return result;
              }, {});

          const sortedNeighborAngles = Object.entries(neighborAngles).sort((a, b) => a[1] - b[1]);
          this.labelAngles[node.id] = this.findWidestAngle(sortedNeighborAngles);
        }
      },
    },
    watch: {
      disablePopover: function (newVal, oldVal) {
        this.graph.nodeLabel(newVal ? null : this.popover);
      },
      edges: function (newEdge, oldEdge) {
        this.onEdgeUpdate();
      },
      nodes: function (newNode, oldNode) {
        this.onNodeUpdate();
      },
      triggerEdgeUpdates: function (newVal, oldVal) {
        this.onEdgeUpdate();
      },
      triggerFitView: function (newVal, oldVal) {
        this.fitGraphToCanvas();
      },
      triggerNodeUpdates: function (newVal, oldVal) {
        this.onNodeUpdate();
      },
      height: function (newVal, oldVal) {
        this.graph.height(newVal);
      },
      selectedNodeIds: {
        handler: async function (newVal, oldVal) {
          this.graph.nodeCanvasObject(this.nodeCanvasObject);
        },
        deep: true,
      },
      width: function (newVal, oldVal) {
        this.graph.width(newVal);
      },
    },
  };
</script>
